import {
  ShiftManagementEnumsRestDayStateEnum,
  ShiftsRestDayFragment,
} from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import type moment from 'moment'
import momentTimezone from 'moment-timezone'

import { dateToString, stringToDate } from 'modules/core/lib/dates'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeLocation } from 'modules/locations/store/collections/locations'

import type View from './View'

export interface Props {
  id: number
  employeeId: number
  date: moment.Moment
  state: ShiftManagementEnumsRestDayStateEnum
  fragment?: ShiftsRestDayFragment
}

export interface RestDayPayload {
  id: number
  employee_id: number
  date: string
  state: ShiftManagementEnumsRestDayStateEnum
}

class RestDay {
  id: number
  employeeId: number
  date: moment.Moment
  state: ShiftManagementEnumsRestDayStateEnum
  fragment?: ShiftsRestDayFragment

  constructor(props: Props) {
    this.id = props.id
    this.employeeId = props.employeeId
    this.date = props.date
    this.state = props.state
    this.fragment = props.fragment

    makeObservable(this, {
      employeeModel: computed,
      isDraft: computed,
      isPublished: computed,
      employeeTimeZone: computed,
      dateWithTZ: computed,
      currentDate: computed,
      hours: computed,
    })
  }

  static buildFromRequest(props: RestDayPayload): RestDay {
    return new RestDay({
      id: props.id,
      employeeId: props.employee_id,
      date: stringToDate(props.date, 'backend_datetime'),
      state: props.state,
    })
  }

  get isDraft(): boolean {
    return this.state === 'draft'
  }

  get isPublished(): boolean {
    return this.state === 'published'
  }

  get employeeModel(): Employee | undefined {
    return employees.find({ id: this.employeeId })
  }

  get employeeTimeZone(): string | null | undefined {
    return this.employeeModel
      ? employeeLocation(this.employeeModel.attributes.location_id)?.timezone
      : null
  }

  get dateWithTZ(): momentTimezone.Moment {
    return momentTimezone.tz(this.date, this.employeeTimeZone ?? '')
  }

  get currentDate(): moment.Moment {
    return !this.employeeTimeZone ? this.date : this.dateWithTZ
  }

  get hours(): moment.Moment[] {
    const startAt = this.currentDate
    const endAt = this.currentDate.clone().add(24, 'h')
    const hours: moment.Moment[] = []
    let hour = startAt

    // Only less than, and not equal to keep the endAt out.
    // Ex. 8:00 - 10:00 => 8:00, 9:00 but 10:00 is out of the range
    while (hour < endAt) {
      hours.push(hour)
      hour = hour.clone().add(1, 'h')
    }

    return hours
  }

  isDate(date: moment.Moment): boolean {
    return dateToString(this.currentDate, 'backend') === dateToString(date, 'backend')
  }

  inRange(firstDay: moment.Moment, lastDay: moment.Moment): boolean {
    return this.currentDate.isBetween(firstDay, lastDay, 'days', '[]')
  }

  datesByView(view: View): moment.Moment[] {
    if (view.isDaily) return this.hours

    return [this.currentDate]
  }
}

export default RestDay
